import type { TaskView } from "@scoach/types";
import { Notebook, Search } from "@scoach/ui/icons";
import { useSearch } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";

import { DashHeader } from "../../dashboard/components/DashHeader.tsx";
import { tasksApi } from "../api.ts";
import { TaskRow } from "./TaskRow.tsx";

type StatusFilter = "all" | "open" | "done";

/** Bucket an open task by its due date for grouped display. */
function dueBucket(due: string): "overdue" | "today" | "week" | "later" | "none" {
  if (!due) return "none";
  const d = new Date(due);
  if (Number.isNaN(d.getTime())) return "none";
  const today = new Date();
  const startToday = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const dd = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const diffDays = Math.round((dd.getTime() - startToday.getTime()) / 86_400_000);
  if (diffDays < 0) return "overdue";
  if (diffDays === 0) return "today";
  if (diffDays <= 7) return "week";
  return "later";
}

const BUCKET_ORDER: { key: ReturnType<typeof dueBucket>; label: string }[] = [
  { key: "overdue", label: "Overdue" },
  { key: "today", label: "Today" },
  { key: "week", label: "This week" },
  { key: "later", label: "Later" },
  { key: "none", label: "No due date" },
];

export function TasksScreen() {
  const { client: initialClient } = useSearch({ from: "/_app/tasks" });
  const [tasks, setTasks] = useState<TaskView[]>([]);
  const [scope, setScope] = useState<"mine" | "team">("mine");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [clientFilter, setClientFilter] = useState<string>(initialClient ?? "");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    const query: Record<string, string> = { scope };
    if (statusFilter !== "all") query.status = statusFilter;
    if (clientFilter) query.client = clientFilter;
    setError(null);
    tasksApi
      .list(query)
      .then((res) => setTasks(res.items))
      .catch((err) => {
        console.error("[tasks] fetch failed", err);
        setError(err instanceof Error ? err.message : "Failed to load tasks");
      })
      .finally(() => setLoading(false));
  }, [statusFilter, clientFilter, scope]);

  const clients = useMemo(() => {
    const set = new Set<string>();
    for (const t of tasks) set.add(t.client);
    return Array.from(set).sort();
  }, [tasks]);

  const searched = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return tasks;
    return tasks.filter((t) =>
      `${t.what} ${t.client} ${t.meetingTitle} ${t.who}`.toLowerCase().includes(q),
    );
  }, [tasks, search]);

  const openTasks = searched.filter((t) => !t.done);
  const doneTasks = searched.filter((t) => t.done);
  const groups = useMemo(() => {
    return BUCKET_ORDER.map((b) => ({
      ...b,
      items: openTasks
        .filter((t) => dueBucket(t.due) === b.key)
        .sort((a, z) => (a.due || "9999").localeCompare(z.due || "9999")),
    })).filter((g) => g.items.length > 0);
  }, [openTasks]);

  function toggleDone(task: TaskView, done: boolean) {
    setTasks((prev) => prev.map((t) => (t.taskId === task.taskId ? { ...t, done } : t)));
    void tasksApi.updateTask(task.taskId, { done }).catch(() => {
      setTasks((prev) => prev.map((t) => (t.taskId === task.taskId ? { ...t, done: !done } : t)));
    });
  }
  function updateTask(task: TaskView, patch: Partial<Pick<TaskView, "who" | "what" | "due">>) {
    const prevVals = { who: task.who, what: task.what, due: task.due };
    setTasks((prev) => prev.map((t) => (t.taskId === task.taskId ? { ...t, ...patch } : t)));
    void tasksApi.updateTask(task.taskId, patch).catch(() => {
      setTasks((prev) => prev.map((t) => (t.taskId === task.taskId ? { ...t, ...prevVals } : t)));
    });
  }

  return (
    <div className="dash">
      <DashHeader />
      <div className="tasks-body">
        <div className="tasks-header">
          <h2 className="tasks-title">
            <Notebook size={20} /> Tasks
          </h2>
          <span className="tasks-count">{tasks.filter((t) => !t.done).length} open</span>
        </div>

        <div className="tasks-filters">
          <div className="seg" style={{ marginRight: 8 }}>
            <button
              type="button"
              className={scope === "mine" ? "on" : ""}
              onClick={() => setScope("mine")}
            >
              My Tasks
            </button>
            <button
              type="button"
              className={scope === "team" ? "on" : ""}
              onClick={() => setScope("team")}
            >
              Team Tasks
            </button>
          </div>

          <div className="seg">
            <button
              type="button"
              className={statusFilter === "all" ? "on" : ""}
              onClick={() => setStatusFilter("all")}
            >
              All
            </button>
            <button
              type="button"
              className={statusFilter === "open" ? "on" : ""}
              onClick={() => setStatusFilter("open")}
            >
              Open
            </button>
            <button
              type="button"
              className={statusFilter === "done" ? "on" : ""}
              onClick={() => setStatusFilter("done")}
            >
              Done
            </button>
          </div>

          <select
            className="tasks-client-filter"
            value={clientFilter}
            onChange={(e) => setClientFilter(e.target.value)}
          >
            <option value="">All clients</option>
            {clients.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>

          <div className="search-box" style={{ marginLeft: "auto" }}>
            <Search size={14} />
            <input
              type="search"
              placeholder="Search tasks…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>

        {error && (
          <div className="tasks-empty" style={{ color: "var(--gc-red, #b00020)" }}>
            {error}
          </div>
        )}
        {loading ? (
          <div className="tasks-empty">Loading tasks...</div>
        ) : !error && searched.length === 0 ? (
          <div className="tasks-empty">
            No tasks found. Action items from meeting summaries will appear here.
          </div>
        ) : (
          <div className="tasks-groups">
            {statusFilter !== "done" &&
              groups.map((g) => (
                <div key={g.key} className="tasks-group">
                  <div className={`tasks-group-head bucket-${g.key}`}>
                    {g.label} <span className="tasks-group-count">{g.items.length}</span>
                  </div>
                  <div className="tasks-cards">
                    {g.items.map((t) => (
                      <TaskRow
                        key={t.taskId}
                        task={t}
                        onToggle={toggleDone}
                        onUpdate={updateTask}
                      />
                    ))}
                  </div>
                </div>
              ))}
            {statusFilter !== "open" && doneTasks.length > 0 && (
              <div className="tasks-group">
                <div className="tasks-group-head bucket-done">
                  Done <span className="tasks-group-count">{doneTasks.length}</span>
                </div>
                <div className="tasks-cards">
                  {doneTasks.map((t) => (
                    <TaskRow key={t.taskId} task={t} onToggle={toggleDone} onUpdate={updateTask} />
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
